import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const empty = { name: "", per_minute_limit: "", per_day_limit: "", allowed_ips: "", expires_at: "" };

const Field = ({ label, hint, children }) => (
  <label className="block space-y-1.5">
    <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{label}</span>
    {children}
    {hint && <span className="block text-xs text-slate-500">{hint}</span>}
  </label>
);

export const KeyFormDialog = ({ open, initial, onClose, onSubmit, busy }) => {
  const [f, setF] = useState(empty);
  useEffect(() => {
    if (!open) return;
    setF(initial ? {
      name: initial.name, per_minute_limit: initial.per_minute_limit ?? "", per_day_limit: initial.per_day_limit ?? "",
      allowed_ips: (initial.allowed_ips || []).join(", "), expires_at: (initial.expires_at || "").slice(0, 10),
    } : empty);
  }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const cls = "bg-[#080C14] border-slate-700 h-10";

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      name: f.name.trim(),
      per_minute_limit: f.per_minute_limit ? Number(f.per_minute_limit) : null,
      per_day_limit: f.per_day_limit ? Number(f.per_day_limit) : null,
      allowed_ips: f.allowed_ips.split(/[\s,]+/).filter(Boolean),
      expires_at: f.expires_at ? `${f.expires_at}T23:59:59Z` : null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="api-key-form-dialog" className="max-w-lg bg-[#0F172A] border-slate-700 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{initial ? "Edit API key" : "Create API key"}</DialogTitle>
          <DialogDescription className="text-slate-400">Leave limits blank to use the defaults (20/min, 500/day).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Name"><Input data-testid="api-key-name-input" required value={f.name} onChange={set("name")} placeholder="e.g. Prime Rural mobile backend" className={cls} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Per minute"><Input data-testid="api-key-per-minute-input" type="number" min="1" value={f.per_minute_limit} onChange={set("per_minute_limit")} placeholder="20" className={cls} /></Field>
            <Field label="Per day"><Input data-testid="api-key-per-day-input" type="number" min="1" value={f.per_day_limit} onChange={set("per_day_limit")} placeholder="500" className={cls} /></Field>
          </div>
          <Field label="Allowed IPs (optional)" hint="Comma-separated. If set, requests from any other IP are rejected.">
            <Input data-testid="api-key-allowed-ips-input" value={f.allowed_ips} onChange={set("allowed_ips")} placeholder="13.235.10.4, 65.0.1.20" className={`${cls} font-mono text-sm`} />
          </Field>
          <Field label="Expires on (optional)"><Input data-testid="api-key-expires-input" type="date" value={f.expires_at} onChange={set("expires_at")} className={cls} /></Field>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} className="text-slate-300 hover:bg-slate-800 hover:text-white">Cancel</Button>
            <Button data-testid="api-key-form-submit" disabled={busy} type="submit" className="bg-indigo-500 hover:bg-indigo-600 active:scale-95 transition-transform">{initial ? "Save changes" : "Create key"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
